import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "src/common/state/hooks";
import { createAndAssignProjectFromAiGraph } from "src/features/projects/state/projectsSlice";
import {
  fetchAndUpdateSpecificConversation,
  generateProjectGroupFromAiGraph,
  getActiveGptConversation,
  setIdOfLatestProjectGroupCursedByAi,
  setLastAiModuleContext,
} from "../state/chatbotSlice";
import { CHATBOT_COMPONENT_ID, createProjectFromAiGraphModalId } from "./Chatbot";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import {
  fetchSearchIndex,
  fetchSpecificProjectGroupWithProjects,
  getProjectGroupSize,
  getSearchIndex,
  setSelectedProjectGroupFromProjectGroupId,
} from "src/features/projectGroups/state/projectGroupsSlice";
import SearchableDropdown from "src/common/components/SearchableDropdown/SearchableDropdown";
import { Tooltip } from "@mui/material";
import { fetchAllCurrentElements } from "src/features/projectGroups/state/projectGroupsSlice";

const leTimestampDuBaguetteGmtPlusDeux = new Date(Date.now() + 2 * 60 * 60 * 1000);
const prettyGenerationDate = new Intl.DateTimeFormat("fr-FR").format(
  leTimestampDuBaguetteGmtPlusDeux,
);

const CreateProjectFromAiGraph = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const activeGptConversation = useSelector(getActiveGptConversation);
  const searchIndex = useSelector(getSearchIndex);
  const [selectedOption, setSelectedOption] = useState("");
  const [name, setName] = useState(`Généré le ${prettyGenerationDate}`);
  const [errorMessage, setErrorMessage] = useState("");
  const [generateBlockadeImages, setGenerateBlockadeImages] = useState(false);
  const [assignedToProjectGroup, setAssignedToProjectGroup] = useState("");
  const [isComputingPgSize, setIsComputingPgSize] = useState(false);
  const [sizeError, setSizeError] = useState("");

  useEffect(() => {
    dispatch(fetchSearchIndex({}));
  }, [dispatch]);

  useEffect(() => {
    if (!assignedToProjectGroup) {
      setSizeError("");
      return;
    }
    const projectIds = searchIndex
      .filter(
        (item) => item.type === "project" && item.projectGroupId === Number(assignedToProjectGroup),
      )
      .map((item) => item.id);
    if (projectIds.length === 0) {
      setSizeError("");
      return;
    }
    setIsComputingPgSize(true);
    setSizeError("");
    dispatch(getProjectGroupSize({ projectsIds: projectIds.join(",") }))
      .then((res: any) => {
        if (res.payload.data > 10737418240) {
          setSizeError(t("tooltip.cannotCreateProjectProjectGroupTooLarge"));
        }
      })
      .finally(() => {
        setIsComputingPgSize(false);
      });
  }, [assignedToProjectGroup]);

  const validateAndSubmit = async () => {
    // Validate the input
    if (!name) {
      setErrorMessage("Le nom ne peut pas être vide.");
      return;
    }
    if (name.length > 24) {
      setErrorMessage("Le nom ne peut pas dépasser 24 caractères.");
      return;
    }
    setErrorMessage("");
    dispatch(closeStandardModal(createProjectFromAiGraphModalId));

    try {
      if (selectedOption === "assignToProjectGroup") {
        // Dispatch action to create and assign project
        const response = (await dispatch(
          createAndAssignProjectFromAiGraph({
            name,
            generateBlockadeImages,
            assignToProjectGroup: Number(assignedToProjectGroup),
            conversationId: activeGptConversation.id,
            relatedGptOutputIndex: activeGptConversation.conversation_data.output.length - 1,
            componentId: CHATBOT_COMPONENT_ID,
            cb: () => {
              dispatch(
                fetchAndUpdateSpecificConversation({ conversationId: activeGptConversation.id }),
              );
              dispatch(
                setAlert({
                  msg: t("pages.chat.createProjectFromAiGraph.moduleGeneratedSuccessfully"),
                  type: "success",
                  isOpen: true,
                  customTimeoutInMs: 15000,
                }),
              );
            },
          }),
        )) as any;

        const projectGroupId = response.payload.data.createdProjectGroupElement?.project_group.id;
        dispatch(fetchAllCurrentElements({ projectGroupId }));
        dispatch(setSelectedProjectGroupFromProjectGroupId(projectGroupId));
        dispatch(
          setIdOfLatestProjectGroupCursedByAi(
            response.payload.data.createdProjectGroupElement?.project_group.id,
          ),
        );

        await dispatch(
          fetchSpecificProjectGroupWithProjects({
            projectGroupId: Number(assignedToProjectGroup),
            componentId: CHATBOT_COMPONENT_ID,
          }),
        );
        dispatch(openStandardModal("notifyAIProjectHasBeenGenerated"));
      }

      if (selectedOption === "generateProjectGroup") {
        // Dispatch action to generate a project group
        const response = (await dispatch(
          generateProjectGroupFromAiGraph({
            name,
            generateBlockadeImages,
            conversationId: activeGptConversation.id,
            relatedGptOutputIndex: activeGptConversation.conversation_data.output.length - 1,
            componentId: CHATBOT_COMPONENT_ID,
          }),
        )) as any;

        const createdProjectGroupId = response.payload.createdProjectGroup?.id;

        await Promise.all([
          dispatch(
            fetchSpecificProjectGroupWithProjects({ projectGroupId: createdProjectGroupId }),
          ),
          dispatch(fetchAllCurrentElements({ projectGroupId: createdProjectGroupId })),
        ]);

        dispatch(setSelectedProjectGroupFromProjectGroupId(createdProjectGroupId));
        dispatch(setIdOfLatestProjectGroupCursedByAi(response.payload.createdProjectGroup?.id));
        dispatch(openStandardModal("notifyAIProjectGroupHasBeenGenerated"));
        dispatch(
          setAlert({
            msg: t("pages.chat.createProjectFromAiGraph.projectGroupGeneratedSuccessfully"),
            type: "success",
            isOpen: true,
            alertShowCloseButton: true,
            customTimeoutInMs: 60000,
          }),
        );
      }
    } catch (error) {
      console.error("Error in validateAndSubmit:", error);
      dispatch(
        setAlert({
          msg: t("pages.chat.createProjectFromAiGraph.errorWhileGenerating"),
          type: "error",
          isOpen: true,
          alertShowCloseButton: true,
        }),
      );
    }
  };

  const handleNavigateToFunnel = () => {
    // Store AI context in Redux for later use
    if (!activeGptConversation?.id) {
      // Show error alert and close modal
      dispatch(
        setAlert({
          title: t("general.error"),
          message: "Conversation non trouvée.",
          severity: "error",
        }),
      );
      dispatch(closeStandardModal(createProjectFromAiGraphModalId));
      console.error("handleNavigateToFunnel: activeGptConversation is null or has no id");
      return;
    }

    // Store AI context for CreateProjectGroupAccess to use in Story 20.3
    dispatch(
      setLastAiModuleContext({
        conversationId: activeGptConversation.id,
        relatedGptOutputIndex: activeGptConversation.conversation_data.output.length - 1,
        generateBlockadeImages,
      }),
    );

    // Close modal first to prevent UI flicker
    dispatch(closeStandardModal(createProjectFromAiGraphModalId));

    // Clear component state to reset form for next use
    setName("");
    setErrorMessage("");
    setGenerateBlockadeImages(false);

    // Navigate to full funnel with cloneAiModule flag
    navigate("/courses/new", { state: { cloneAiModule: true } });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Backspace") {
      event.stopPropagation();
    }
  };

  const InputWithCheckbox = ({ customElement }: { customElement: any }) => (
    <div className="flex flex-col items-start mb-4">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t("pages.chat.createProjectFromAiGraph.enterName")}
        className="border rounded px-3 py-2 w-full mb-4"
        autoFocus
      />
      {customElement}
      <div className="flex items-center hidden">
        {/* Hidden: Feature not yet ready for production. Requires backend API stability improvements
            and content generation validation before enabling. Roadmap: Q2 2026 (tentative). */}
        <h2 className="text-[0.6rem] mr-2 text-center">
          {t("pages.chat.createProjectFromAiGraph.generateBlockadeImages")}
        </h2>
        <input
          type="checkbox"
          checked={generateBlockadeImages}
          onChange={() => setGenerateBlockadeImages(!generateBlockadeImages)}
          style={{
            height: "11px",
            width: "11px",
            border: "1px solid rgb(209 213 219)",
            cursor: "pointer",
          }}
        />
      </div>
    </div>
  );

  const AssignToProjectGroup = () => (
    <div className="w-full flex flex-col items-center justify-center">
      <h2 className="text-lg font-bold mb-4">
        {t("pages.chat.createProjectFromAiGraph.nameAndAssignNewModule")}
      </h2>
      <InputWithCheckbox
        customElement={
          <div className="mb-4 w-full">
            <SearchableDropdown
              options={searchIndex.filter((item) => item.type === "projectGroup")}
              selectedId={assignedToProjectGroup}
              onSelect={(selectedId) => {
                setAssignedToProjectGroup(selectedId.toString());
              }}
              placeholder={t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")}
              searchPlaceholder={t("pages.chat.createProjectFromAiGraph.searchAProjectGroup")}
              noItemFound={t("pages.chat.createProjectFromAiGraph.noCourseFound")}
            />
          </div>
        }
      />
      {errorMessage && <p className="text-red-500 text-sm mb-2">{errorMessage}</p>}
      {sizeError && <p className="text-red-500 text-sm mb-2">{sizeError}</p>}

      <Tooltip
        title={
          sizeError
            ? sizeError
            : isComputingPgSize
            ? t("tooltip.computingPgSize")
            : assignedToProjectGroup
            ? ""
            : t("pages.chat.createProjectFromAiGraph.selectAProjectGroup")
        }
      >
        <button
          className="btn-primary-fill"
          disabled={!assignedToProjectGroup || isComputingPgSize || !!sizeError}
          onClick={validateAndSubmit}
        >
          {t("general.generate")}
        </button>
      </Tooltip>
    </div>
  );

  const GenerateProjectGroup = () => (
    <div className="w-full flex flex-col items-center justify-center">
      <h2 className="text-lg font-bold mb-4">
        {t("pages.chat.createProjectFromAiGraph.nameNewProjectGroup")}
      </h2>
      <InputWithCheckbox customElement={null} />
      {errorMessage && <p className="text-red-500 text-sm mb-2">{errorMessage}</p>}
      <button className="btn-primary-fill" onClick={handleNavigateToFunnel}>
        {t("general.generate")}
      </button>
    </div>
  );

  const renderAppropriateComponent = () => {
    switch (selectedOption) {
      case "assignToProjectGroup":
        return <AssignToProjectGroup />;
      case "generateProjectGroup":
        return <GenerateProjectGroup />;
      case "":
      default:
        return (
          <div className="w-full flex flex-col items-center justify-center">
            <h2 className="text-lg font-bold mb-4">
              {t("pages.chat.createProjectFromAiGraph.title")}
            </h2>
            <div className="flex w-3/5 items-center justify-between">
              <button className="btn-primary-fill" onClick={handleNavigateToFunnel}>
                {t("pages.chat.createProjectFromAiGraph.generateProjectGroup")}
              </button>
              <button
                className="btn-primary-fill"
                onClick={() => setSelectedOption("assignToProjectGroup")}
              >
                {t("pages.chat.createProjectFromAiGraph.assignToProjectGroup")}
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="w-full flex flex-col items-center justify-center">
      {renderAppropriateComponent()}
    </div>
  );
};

export default CreateProjectFromAiGraph;
